import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Layout from '../../Component/layout';
import NoAccess from '../../Component/NoAccess';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';
import {
  HUMAN_FLAGS,
  HUMAN_GRADES,
  getVoiceCallForReview,
  isApiError,
  recordVoiceCallGrade
} from '../../Redux/util/Apis/voiceCallAudit';
import type {
  HumanFlag,
  HumanGrade,
  VoiceCallDetail
} from '../../Redux/util/Apis/voiceCallAudit';

/**
 * Voice-call QA review screen (ZUM-3872).
 *
 * ZUM-3872 stops posting green calls to #ivr-call-recordings, so ~85% of calls
 * no longer appear anywhere a reviewer can react to them. Looker's Voice Call
 * Audit explore lists and filters every call and links one row at a time to
 * here; this screen plays it and records the verdict.
 *
 * It is deliberately a SINGLE-CALL page: no table, no filters, no pagination.
 * Looker already is that table, and duplicating it here would mean two places
 * to keep in step for no gain.
 *
 * **The SUPER_ADMIN check below is here, not in the router.** `PrivateRoute`
 * declares an `acl` prop and never reads it — it only checks that the user has
 * *a* role — so the entry in `routesConfig` is documentation, not enforcement.
 * This screen shows recordings and transcripts for ANY client with no tenant
 * scoping, and rb-frontend is a client-facing portal, so it needs a real check.
 * Making `PrivateRoute` honour `acl` globally would be the better fix and is
 * deliberately not done here: several existing routes would start rejecting
 * roles they currently admit, which is a change well outside this ticket.
 *
 * The guard that actually protects the DATA is on the rb-backend-apis
 * endpoints (`authorizeSuperAdmin`). This one stops the page rendering.
 */

const GRADE_COLOURS: Record<string, string> = {
  green: '#2e7d32',
  yellow: '#ed6c02',
  red: '#d32f2f'
};

const GRADE_EMOJI: Record<string, string> = {
  green: '🟢',
  yellow: '🟡',
  red: '🔴',
  inconclusive: '❓',
  test: '❌'
};

/**
 * Render a three-valued QA fact.
 *
 * `transferred`, `transfer_answered` and `tour_booked` are all `boolean | null`,
 * and NULL means UNKNOWN, never "no". They are written at StreamEnd, which fires
 * when the `<Dial>` to the office *starts* — the outcome lands on Twilio
 * callbacks minutes later, and `_transferred` returns None outright when
 * voice-adapter's teardown has not arrived.
 *
 * Collapsing that to "no" tells a reviewer the call was definitively not
 * transferred when the system does not know, on the screen where they are
 * judging transfer handling — the largest human-flagged defect category. Shared
 * so the three cannot drift apart again.
 */
const describeTriState = (value: boolean | null | undefined) =>
  value == null ? 'not yet known' : value ? 'yes' : 'no';

/** A judge verdict with no colour is not an unremarkable call — see below. */
const describeJudgeGrade = (grade: string | null) =>
  grade ? `${GRADE_EMOJI[grade] ?? ''} ${grade}`.trim() : 'not graded';

/**
 * Whether a response body is one this screen can actually render.
 *
 * It names EXACTLY the three things the render dereferences without protection
 * — `judge.*`, `call.*` and `humanGrades.find` — rather than a single field
 * standing in for "loaded". Validating one of the three and not the others is
 * arbitrary, and the compiler will not close the gap: `strictNullChecks` is off
 * in this repo, so a missing nested object is not a type error here.
 *
 * Our own API cannot produce a partial body — `getCallForReview` returns either
 * null or an object literal carrying all three — so this guards a contract
 * change or something rewriting the response in transit, not a live path.
 */
const isRenderableDetail = (value: unknown): value is VoiceCallDetail => {
  const detail = value as VoiceCallDetail | null;
  return (
    !!detail &&
    !!detail.judge &&
    !!detail.call &&
    Array.isArray(detail.humanGrades)
  );
};

/**
 * One labelled fact, sized on a 12-column grid so the row actually lines up.
 * The previous version was a wrapping Stack of min-width boxes, which produced a
 * ragged second row whenever a value was long.
 */
const Fact = ({
  label,
  children,
  xs = 6,
  sm = 4,
  md = 3
}: {
  label: string;
  children?: React.ReactNode;
  xs?: number;
  sm?: number;
  md?: number;
}) => (
  <Grid item xs={xs} sm={sm} md={md}>
    <Typography
      variant="overline"
      component="div"
      sx={{ color: 'text.secondary', lineHeight: 1.6 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {children ?? '—'}
    </Typography>
  </Grid>
);

/** Every section is the same card, so the page has one visual rhythm. */
const Section = ({
  title,
  action,
  children
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: action ? 0 : 1.5 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const VoiceCallAudit = (props: { history?: unknown }) => {
  const { callSid } = useParams<{ callSid: string }>();
  const { userData } = useContext(GlobalContext);
  const isSuperAdmin = userData?.role?.name === 'SUPER_ADMIN';

  const [detail, setDetail] = useState<VoiceCallDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [grade, setGrade] = useState<HumanGrade | null>(null);
  const [notes, setNotes] = useState('');
  const [flags, setFlags] = useState<Partial<Record<HumanFlag, boolean>>>({});
  // The judge's verdict is COLLAPSED by default, deliberately. Seeing a grade
  // before forming your own is the anchoring that `grade_mode` exists to keep
  // out of the agreement number — and on a call drawn from the unbiased sample
  // queue, an anchored grade is worse than no grade, because it looks like data.
  // Listen first, grade, then open this to compare.
  const [judgeOpen, setJudgeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getVoiceCallForReview(callSid);
      if (isApiError(result)) {
        setLoadError(
          result.status === 404
            ? 'No AI-answered call found for that CallSid. Legacy zuma-voiceai calls and calls that never reached the AI have no judge row.'
            : result.message
        );
        setDetail(null);
      } else if (!isRenderableDetail(result)) {
        // A 2xx carrying a body this screen cannot render. Checked AFTER
        // `isApiError`, not before: an ApiError has no `judge` either, and
        // testing for it first swallowed the 404 into this generic message.
        //
        // Without this branch the next line throws on `result.humanGrades`,
        // `setLoading(false)` never runs, and the screen sits on the spinner for
        // ever with only an unhandled rejection to explain it.
        setLoadError(
          'The server returned no details for this call. Try again, and report it if it persists.'
        );
        setDetail(null);
      } else {
        setDetail(result);
        // Seed the form from any verdict this reviewer's surface already holds,
        // so re-opening a call shows what was recorded rather than a blank form
        // that looks like it was never graded.
        const portal = (result.humanGrades || []).find(
          g => g.source === 'portal'
        );
        if (portal) {
          setGrade(portal.grade);
          setNotes(portal.notes || '');
          setFlags(
            HUMAN_FLAGS.reduce(
              (acc, f) => {
                // Types cleanly without a cast: HumanFlag is exactly the six
                // optional booleans on VoiceCallHumanGrade. The previous cast
                // through Record<string, unknown> was unsound, and only
                // survived because nothing was type-checking this file.
                acc[f.key] = portal[f.key] === true;
                return acc;
              },
              {} as Partial<Record<HumanFlag, boolean>>
            )
          );
        }
      }
    } finally {
      // In a `finally` so an unexpected throw above cannot leave the screen on
      // the spinner. A visible error is recoverable; a permanent spinner is not.
      setLoading(false);
    }
  }, [callSid]);

  useEffect(() => {
    if (isSuperAdmin) {
      load();
    }
  }, [load, isSuperAdmin]);

  const onSave = async () => {
    setSaving(true);
    setSaveError(null);
    const result = await recordVoiceCallGrade(callSid, {
      grade: grade as HumanGrade,
      notes,
      flags
    });
    if (isApiError(result)) {
      setSaveError(result.message);
    } else {
      setSavedAt(new Date());
      // Re-read rather than patching local state: the server decides the
      // provenance, and showing a stale or guessed one would misrepresent
      // whether this grade counts toward the published recall number.
      await load();
    }
    setSaving(false);
  };

  if (!isSuperAdmin) {
    return <NoAccess />;
  }

  if (loading) {
    return (
      <Layout history={props.history}>
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout history={props.history}>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      </Layout>
    );
  }

  if (!detail) {
    // Unreachable via `load`, which sets `loadError` whenever it clears `detail`.
    // Kept because `strictNullChecks` is off in this repo, so the compiler will
    // NOT catch a future path that clears one without the other — and the cost
    // of being wrong here is a blank screen on a QA tool.
    return (
      <Layout history={props.history}>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">No details available for this call.</Alert>
        </Box>
      </Layout>
    );
  }

  const { judge, call, humanGrades, inReviewSample, recordingUrl } = detail;
  // `?? []`, not a destructuring default: a default only fires on `undefined`,
  // so an explicit `null` would still throw on `.find` below.
  const slackGrade = (humanGrades ?? []).find(g => g.source === 'slack');

  return (
    <Layout history={props.history}>
      <Box sx={{ p: 3, maxWidth: 980 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Voice call review
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {call.client || '—'} &middot; {call.community || '—'} &middot;{' '}
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {callSid}
          </Box>
        </Typography>

        {inReviewSample && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>This call is in the review sample.</strong> It was chosen by
            a hash of its CallSid, not by anyone&apos;s judgement, which is what
            makes the grades from this queue an unbiased measure of how often
            the judge passes a call it should not have. Grading it here counts
            toward the published recall number.
          </Alert>
        )}

        <Section title="Recording">
          {/* A pure function of the CallSid, so it plays even for a call the
              warehouse has not caught up with. */}
          {/* `metadata`, not `none`: the duration is decision-relevant — a
              20-second hangup and a six-minute call want different attention —
              and with `none` the control reads 0:00 / 0:00 until you commit to
              playing. Only the header is fetched, not the audio. */}
          <audio controls preload="metadata" style={{ width: '100%' }}>
            <source src={recordingUrl} type="audio/wav" />
            <track kind="captions" />
          </audio>
          <Link
            href={recordingUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{ display: 'inline-block', mt: 1 }}
          >
            Open in a new tab
          </Link>
        </Section>

        <Section title="What actually happened">
          <Grid container spacing={2}>
            <Fact label="Turns">{judge.n_turns}</Fact>
            <Fact label="AI duration">
              {judge.ai_duration_s == null ? '—' : `${judge.ai_duration_s}s`}
            </Fact>
            <Fact label="Whole call">
              {judge.duration_s == null ? '—' : `${judge.duration_s}s`}
            </Fact>
            <Fact label="Call result" md={3}>
              {call.callResult}
            </Fact>
            <Fact label="Transferred">
              {describeTriState(judge.transferred)}
              {judge.transfer_status ? ` (${judge.transfer_status})` : ''}
            </Fact>
            <Fact label="Transfer answered">
              {describeTriState(judge.transfer_answered)}
            </Fact>
            <Fact label="Tour booked">
              {describeTriState(judge.tour_booked)}
            </Fact>
            <Fact label="STT">{judge.stt_provider}</Fact>
            <Fact label="Overtalk turns">{judge.overtalk_turns}</Fact>
            <Fact label="Caller-quiet re-prompts">
              {judge.silence_watchdog_count}
            </Fact>
            <Fact label="Slow turns">{judge.slow_ttft_turns}</Fact>
            <Fact label="Interrupted turns">{judge.interrupted_turns}</Fact>
          </Grid>
        </Section>

        {call.summary && (
          <Section title="Summary">
            <Typography variant="body2">{call.summary}</Typography>
          </Section>
        )}

        {call.transcript && (
          <Section title="Transcript">
            <Box
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                backgroundColor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: 13,
                lineHeight: 1.6
              }}
            >
              {call.transcript}
            </Box>
          </Section>
        )}

        {slackGrade && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Graded <strong>{slackGrade.grade}</strong> in #ivr-call-recordings
            {slackGrade.notes ? ` — \u201c${slackGrade.notes}\u201d` : ''}. Your
            grade here is recorded separately; it does not overwrite that one.
          </Alert>
        )}

        <Section title="Your grade">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={grade}
            onChange={(_e, next) => next && setGrade(next)}
            sx={{ mb: 2 }}
          >
            {HUMAN_GRADES.map(g => (
              <ToggleButton key={g} value={g} sx={{ px: 3 }}>
                {GRADE_EMOJI[g]}&nbsp;{g}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <FormGroup row sx={{ mb: 1 }}>
            {HUMAN_FLAGS.map(f => (
              <FormControlLabel
                key={f.key}
                control={
                  <Checkbox
                    size="small"
                    checked={flags[f.key] === true}
                    onChange={e =>
                      setFlags(prev => ({ ...prev, [f.key]: e.target.checked }))
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    {f.emoji}&nbsp;{f.label}
                  </Typography>
                }
              />
            ))}
          </FormGroup>

          <TextField
            label="Notes"
            placeholder="What did you actually hear?"
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            inputProps={{ maxLength: 4000 }}
            sx={{ mb: 2 }}
          />

          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          {savedAt && !saveError && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Saved.
            </Alert>
          )}

          <Button
            variant="contained"
            disabled={!grade || saving}
            onClick={onSave}
          >
            {saving ? 'Saving…' : 'Save grade'}
          </Button>
        </Section>

        {/* LAST on the page and collapsed by default. Reading a grade before
            forming your own is the anchoring `grade_mode` exists to keep out of
            the agreement number, and on a sampled call an anchored grade is
            worse than none because it still looks like data. */}
        <Section
          title="The judge"
          action={
            <Button
              size="small"
              onClick={() => setJudgeOpen(o => !o)}
              endIcon={judgeOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {judgeOpen ? 'Hide' : 'Reveal'}
            </Button>
          }
        >
          {!judgeOpen && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Hidden until you ask. Listen and grade first — seeing the verdict
              beforehand is what makes a grade unusable for measuring the judge.
            </Typography>
          )}
          <Collapse in={judgeOpen} unmountOnExit>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Fact label="Grade">
                <Box
                  component="span"
                  sx={{ color: GRADE_COLOURS[judge.grade] }}
                >
                  {describeJudgeGrade(judge.grade)}
                </Box>
              </Fact>
              <Fact label="Confidence">
                {judge.judge_confidence == null
                  ? '—'
                  : judge.judge_confidence.toFixed(2)}
              </Fact>
              <Fact label="Mode">{judge.grade_mode}</Fact>
              <Fact label="Prompt version">{judge.judge_prompt_version}</Fact>
            </Grid>

            {judge.grade == null && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                The judge did not answer on this call. That is not a pass — it
                means grading was off for the client, or the judge failed.
              </Alert>
            )}

            {judge.diagnosis && (
              <Typography
                variant="body2"
                sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}
              >
                &ldquo;{judge.diagnosis}&rdquo;
              </Typography>
            )}

            {(judge.categories || []).length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1.5 }}
                flexWrap="wrap"
              >
                {judge.categories.map(c => (
                  <Chip key={c} label={c} size="small" variant="outlined" />
                ))}
              </Stack>
            )}
          </Collapse>
        </Section>
      </Box>
    </Layout>
  );
};

export default VoiceCallAudit;
